import { useGetUsersQuery, useGetContractorsListQuery } from "./usersApiSlice";
import { useApproveUserMutation, useRejectUserMutation } from "../contractors/contractorsApiSlice";
import { Link } from "react-router-dom";
import { PAGE_SIZE } from "../../constants";
import { NAV } from "../../navLabels";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Header,
  Button,
  Icon,
  Loader,
  Table,
  Grid,
  Pagination,
  Container,
  Image,
  Input
} from "semantic-ui-react";
import React, { useState, useEffect } from "react";
import { Badge, Pill, ui } from "../../components/adminUi";
import '../../components/SharedCard.css';
import CustomDropdown from "../../components/CustomDropdown";
import CustomSnackbar from "../../components/CustomSnackbar";
import { baseUrl } from "../../app/api/apiSlice";

/**
 * A Firebase UID is not an identity. 14 of prod's 32 users sign in socially and carry a
 * 28-char UID as their username, which occupied a whole column and identified nobody.
 * Prefer the email; fall back to the username only when a human could read it.
 */
const looksLikeUid = value =>
  !!value &&
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ||
    /^[A-Za-z0-9]{20,}$/.test(value));

export const accountLabel = user => {
  if (user?.email) return user.email;
  if (user?.username && !looksLikeUid(user.username)) return user.username;
  return '';
};

const PROVIDER_ICONS = {
  'google.com': { name: 'google', color: '#4285F4', title: 'Google' },
  'apple.com': { name: 'apple', color: '#1d1d1f', title: 'Apple' },
};

const ProviderIcon = ({ provider }) => {
  const it = PROVIDER_ICONS[provider] || { name: 'mail outline', color: '#86868b', title: '이메일' };
  return (
    <Icon name={it.name} title={it.title} style={{ color: it.color, marginRight: '7px', opacity: 0.85 }} />
  );
};

const scoreColor = score =>
  score >= 80 ? '#1A7F4B' : score >= 50 ? '#B25E00' : '#C0392B';

/** `practice_seconds_used` is null only when the queryset did not annotate it. */
const practiceExhausted = user =>
  user?.practice_seconds_used != null &&
  user?.practice_budget_minutes > 0 &&
  user.practice_seconds_used >= user.practice_budget_minutes * 60;

const practiceMinutes = user =>
  user?.practice_seconds_used == null ? null : Math.round(user.practice_seconds_used / 60);

const formatActivity = user => {
  const mins = practiceMinutes(user);
  return `연습 ${mins == null ? '—' : `${mins}분`} / 평가 ${user?.evaluation_session_count ?? 0}회`;
};

const UsersList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const page_size = queryParams.get("page_size") || PAGE_SIZE;
  const ordering = queryParams.get("ordering");
  const search = queryParams.get("search");
  const contractor = queryParams.get("contractor");

  const [searchValue, setSearchValue] = useState(search || '');

  // get query params from frontend url -> remove null ones to pass to backend
  let qp = { page, page_size, ordering, search, contractor };
  qp = Object.entries(qp)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

  const { data, isLoading, isSuccess, isError, error } = useGetUsersQuery({
    ...qp,
  });

  const { data: contractorsData } = useGetContractorsListQuery();
  const contractors = Array.isArray(contractorsData)
    ? contractorsData
    : contractorsData?.results || [];
  const contractorOptions = contractors.map(c => ({
    key: c.id,
    text: c.name,
    value: c.id,
  }));

  const [viewMode, setViewMode] = useState('table'); // Default to table for users
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const currentUser = useSelector(state => state.auth.user);
  const isAdminUser = currentUser?.is_staff || currentUser?.is_superuser || currentUser?.is_contractor_admin;
  const [approveUser] = useApproveUserMutation();
  const [rejectUser] = useRejectUserMutation();


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // 직원 목록 Excel (#197). Same fetch-then-blob download as 대화 내역's export: the API is
  // bearer-authenticated, so a plain <a href> would 401. Filters as on screen, no paging.
  const token = useSelector(state => state.auth.token);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(qp);
      params.delete('page');
      params.delete('page_size');
      const response = await fetch(`${baseUrl}/api/v1/accounts/users/export/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const stamp = new Date().toLocaleDateString('sv-SE').replace(/-/g, '');
      const url = window.URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err) {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const getSortIcon = column => {
    if (ordering === column) return 'sort up';
    if (ordering === `-${column}`) return 'sort down';
    return 'sort';
  };

  if (isLoading) return <Loader active />;
  if (isError) return null;

  return (
    <div
      style={{
        // backgroundColor: '#f9fafb',
        // minHeight: '100vh',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Container fluid={viewMode === 'table'}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                flexShrink: 0, // stays a circle when the text beside it wraps
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#F2F2F7', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name="users" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>{NAV.MY_STAFF}</h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>직원 현황, 가입 승인, 학습 기록</p>
              </div>
            </div>

          </div>
        </Container>
      </div>

      <Container
        fluid={viewMode === 'table'}
        style={{
          padding: isMobile ? '0 20px' : '20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          {/* Partner Dropdown */}
          <CustomDropdown
            placeholder="소속 선택"
            options={contractorOptions}
            value={contractor}
            onChange={value => handleFilterChange('contractor', value)}
            icon="building outline"
            style={{ minWidth: '200px' }}
          />

          {/* Search Input */}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'flex-start',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              height: '48px',
              gap: '12px',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon
              name="search"
              style={{ color: '#86868b', margin: 0, fontSize: '16px' }}
            />
            <Input
              placeholder="검색 (이름, 이메일, 사용자명)..."
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              transparent
              fluid
              style={{
                fontSize: '13px',
                width: '100%',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            {searchValue && (
              <Icon
                name="close"
                link
                onClick={() => {
                  setSearchValue('');
                  handleFilterChange('search', null);
                }}
                style={{ color: '#86868b', margin: 0, display: 'flex', alignItems: 'center' }}
              />
            )}
          </div>
          {/* Identical to 대화 내역's search button. It differed in four ways at once —
              12px radius vs a 30px pill, #007AFF vs adminUi's designated CTA blue #0091FF,
              a leading magnifier vs a trailing arrow, and `content=` vs children — which is
              a lot of drift for the same control on two pages that sit next to each other
              in the menu. */}
          <Button
            primary
            icon
            labelPosition="right"
            onClick={handleSearch}
            fluid={isMobile}
            style={{
              borderRadius: '30px',
              height: '48px',
              backgroundColor: ui.blue,
              paddingLeft: '16px',
              paddingRight: '16px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            검색
            <Icon name="arrow right" style={{ marginLeft: '8px', margin: 0 }} />
          </Button>
          {isAdminUser && (
            <Button
              icon
              labelPosition="right"
              loading={exporting}
              disabled={exporting}
              onClick={handleExport}
              fluid={isMobile}
              style={{
                borderRadius: '30px',
                height: '48px',
                backgroundColor: '#F2F2F7',
                color: '#1d1d1f',
                paddingLeft: '20px',
                paddingRight: '20px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              엑셀 다운로드
              {/* Not "file excel outline": at this size its ✕-on-a-page glyph reads as a broken file. */}
              <Icon name="download" style={{ margin: 0 }} />
            </Button>
          )}
        </div>

      {isMobile ? (
          <Grid style={{ height: '100%', margin: 0 }} container={false}>
            <Grid.Column width={16} style={{ height: '100%', overflowY: 'auto', paddingBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {data.results.map((user) => (
                    <div key={user.id} className="custom-card">
                        <div className="custom-card-header" style={{ justifyContent: 'space-between' }}>
                            <h3 className="custom-card-title">{user.full_name || user.username}</h3>
                            <Image
                                src={user.avatar_url || 'https://react.semantic-ui.com/images/avatar/large/matthew.png'} 
                                avatar
                            />
                        </div>
                        {/* Same rule as the table's 계정 column — a Firebase UID is not
                            an identity, so an account with only one shows nothing. */}
                        <div className="custom-card-meta">{accountLabel(user) || '—'}</div>
                        
                        <div className="custom-card-content" style={{ flexGrow: 1 }}>
                            {user.is_staff && <Badge tone="red">Admin</Badge>}
                            <div className="custom-card-details">
                                <div className="custom-card-detail-item">
                                  <Icon name="building outline" />
                                  <span><b>파트너:</b> {user.contractor ? user.contractor.name : '-'}</span>
                                </div>
                                <div className="custom-card-detail-item">
                                  <Icon name="calendar alternate outline" />
                                  <span><b>가입일:</b> {moment(user.created_at).format("YYYY-MM-DD")}</span>
                                </div>
                                <div className="custom-card-detail-item">
                                  <Icon name="clock outline" />
                                  <span><b>연습:</b> {practiceMinutes(user) == null ? '—' : `${practiceMinutes(user)}분`}</span>
                                </div>
                                <div className="custom-card-detail-item">
                                  <Icon name="clipboard check" />
                                  <span><b>평가:</b> {user.evaluation_session_count ?? 0}회</span>
                                </div>
                                <div className="custom-card-detail-item">
                                  <Icon name="star outline" />
                                  <span><b>평균 점수:</b> {user.average_evaluation_score ? user.average_evaluation_score.toFixed(1) : '-'}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="custom-card-actions">
                            <Button 
                                fluid 
                                size="small" 
                                className="custom-card-btn-primary" 
                                as={Link} 
                                to={`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&trainee=${user.id}`}
                            >
                                채팅 세션
                            </Button>
                            <Button 
                                fluid 
                                size="small" 
                                className="custom-card-btn-secondary" 
                                as={Link} 
                                to={`/userdashboard?userId=${user.id}`}
                            >
                                대시보드
                            </Button>
                        </div>
                    </div>
                ))}
              </div>
            </Grid.Column>
          </Grid>
      ) : (
        <div style={{ height: '100%', margin: 0 }}>
            <div
              style={{
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflowY: 'auto',
                  // maxHeight: '100%',
              }}
            >
              <div>
                <Table compact="very" style={{ margin: 0, border: 'none' }}>
                  <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                    <Table.Row>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)', cursor: 'pointer', paddingLeft: '20px' }} onClick={() => handleSort('full_name')}>
                        이름 <Icon name={getSortIcon('full_name')} />
                      </Table.HeaderCell>
                      {/* 사용자명 + 이메일 merged. For social sign-ins the username is a
                          Firebase UID — 14 of prod's 32 users — which identified nobody and
                          cost a column. `accountLabel` picks the email, falls back to a
                          readable username, and shows nothing for UID-only accounts. */}
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)', cursor: 'pointer' }} onClick={() => handleSort('email')}>
                        계정 <Icon name={getSortIcon('email')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>권한</Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>승인</Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)', cursor: 'pointer' }} onClick={() => handleSort('contractor__name')}>
                        소속 <Icon name={getSortIcon('contractor__name')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)', cursor: 'pointer' }} onClick={() => handleSort('created_at')}>
                        가입일 <Icon name={getSortIcon('created_at')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)', cursor: 'pointer' }} onClick={() => handleSort('average_evaluation_score')}>
                        평균 점수 <Icon name={getSortIcon('average_evaluation_score')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>
                        활동
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>액션</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {data.results.map((user) => (
                        <Table.Row key={user.id}>
                            <Table.Cell style={{ paddingLeft: '20px' }}>
                                <Header as='h4' image>
                                    <Image src={user.avatar_url || 'https://react.semantic-ui.com/images/avatar/small/matthew.png'} rounded size='mini' />
                                    <Header.Content>
                                        {user.full_name || <span style={{ color: ui.muted, fontWeight: 400 }}>이름 없음</span>}
                                    </Header.Content>
                                </Header>
                            </Table.Cell>
                            <Table.Cell style={{ color: accountLabel(user) ? ui.text : ui.muted }}>
                                {/* Provider as a bare glyph, not a chip: it was a coloured
                                    label on every row saying the same three things. */}
                                <ProviderIcon provider={user.sign_in_provider} />
                                {accountLabel(user) || '—'}
                            </Table.Cell>
                            <Table.Cell>
                                {/* Only when it is not the default. "User" on every row was
                                    a chip that never varied. */}
                                {user.is_staff ? <Badge tone="red">Admin</Badge> : <span style={{ color: ui.muted }}>—</span>}
                            </Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Badge tone={user.is_approved ? 'green' : 'amber'}>
                                        {user.is_approved ? '승인됨' : '대기중'}
                                    </Badge>
                                    {/* The one saturated thing in the row, so it reads as
                                        the action rather than another status chip. */}
                                    {isAdminUser && !user.is_approved && (
                                      <Pill variant="primary" onClick={() => approveUser(user.id)}>승인</Pill>
                                    )}
                                    {isAdminUser && user.is_approved && (
                                      <Pill variant="ghost" onClick={() => rejectUser(user.id)}>취소</Pill>
                                    )}
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                {user.contractor ? user.contractor.name : '-'}
                            </Table.Cell>
                            <Table.Cell>{moment(user.created_at).format("YYYY-MM-DD")}</Table.Cell>
                            <Table.Cell>
                                {/* A coloured number, not a chip: the value is the point
                                    and the colour is the grading. */}
                                {user.average_evaluation_score ? (
                                    <span style={{ fontWeight: 600, color: scoreColor(user.average_evaluation_score) }}>
                                        {user.average_evaluation_score.toFixed(1)}
                                    </span>
                                ) : (
                                    <span style={{ color: ui.muted }}>—</span>
                                )}
                            </Table.Cell>
                            <Table.Cell>
                                {/* One line: 연습 18분 / 평가 2회. The budget denominator
                                    moved to the tooltip — it is the same for everyone, so
                                    repeating "/ 40분" on every row spent width to say one
                                    constant. The colour still carries "spent". */}
                                <span
                                    title={`사전 연습 ${user.practice_budget_minutes ?? 0}분 중 사용`}
                                    style={{ fontSize: '13px', color: practiceExhausted(user) ? ui.redFg : ui.text }}
                                >
                                    {formatActivity(user)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <Link to={`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&trainee=${user.id}`} title="대화 내역">
                                        <Pill variant="neutral" style={{ padding: '7px 10px' }}>
                                            <Icon name="comments outline" style={{ margin: 0 }} />
                                        </Pill>
                                    </Link>
                                    <Link to={`/userdashboard?userId=${user.id}`} title="대시보드">
                                        <Pill variant="neutral" style={{ padding: '7px 10px' }}>
                                            <Icon name="chart bar" style={{ margin: 0 }} />
                                        </Pill>
                                    </Link>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              
              <div style={{ 
                padding: '12px', 
                borderTop: '1px solid rgba(0,0,0,0.05)', 
                display: 'flex', 
                justifyContent: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                <Pagination
                    activePage={page}
                    onPageChange={handlePaginationChange}
                    totalPages={Math.ceil(data.count / page_size)}
                    size="mini"
                    firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                    lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                    prevItem={{ content: <Icon name='angle left' />, icon: true }}
                    nextItem={{ content: <Icon name='angle right' />, icon: true }}
                />
              </div>
            </div>
        </div>
      )}
      <CustomSnackbar
        visible={exportFailed}
        title="다운로드 실패"
        message="엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요."
        onClose={() => setExportFailed(false)}
        type="error"
      />
    </Container>
    </div>
  );
};
export default UsersList;
